'use strict';

/**
 * shouldFetchDynamic(appTemplate, data) — pure decision for whether the app
 * preview should fetch a live feed (getDynamic) for the current app.data.
 *
 * Extracted from app-preview-wrapper.js's $watch('$ctrl.app.data') block, which
 * previously carried per-slug `if` branches flagged "hard-coding app specific
 * logic - this must be moved". Moving it here (a) lets Achievers/Workday join the
 * dispatch and (b) makes the rules unit-testable without angular-mocks, matching
 * the connect-strategy.js precedent from #634.
 *
 * Rules are keyed by appTemplate.slug. An app absent from the table never
 * triggers a dynamic fetch (its preview is driven by the `cache` channel alone).
 *
 * @param {{slug?: string}|null|undefined} appTemplate
 * @param {object|null|undefined} data - the app.data model (formly `fields.data`)
 * @returns {boolean}
 */
function getDynamicFetchKey(appTemplate, data, integrationId) {
  const dataKey = getDynamicDataKey(appTemplate, data);
  if (!dataKey) return null;

  // #617 — the upstream request is made against a specific integration account,
  // so the account is part of the request's identity. Without it, switching from
  // account A to B without touching the feed settings produced an identical key:
  // the tracker suppressed the second request and an in-flight response from A
  // was still accepted, leaving A's content on screen under B.
  return integrationId === undefined || integrationId === null
    ? dataKey
    : `${dataKey}@${integrationId}`;
}

function getDynamicDataKey(appTemplate, data) {
  if (!appTemplate || !appTemplate.slug || !data) return null;

  switch (appTemplate.slug) {
    case 'twitter-hashtag':
      return data.hashtag && data.hashtag.length >= 2
        ? `twitter-hashtag:${data.hashtag}`
        : null;

    case 'twitter-timeline':
      return data.username && data.username.length >= 2
        ? `twitter-timeline:${data.username}`
        : null;

    case 'dropbox-gallery':
      return data.folder && data.folder.length >= 1
        ? `dropbox-gallery:${data.folder}`
        : null;

    case 'message-board':
      return data.useBucketList && data.url
        ? `message-board:${data.url}`
        : null;

    // #561/#579 — Achievers: one of four explicit whatToPlay values selects the upstream feed family. All
    // remaining data fields are presentation inputs applied to the cached feed.
    case 'achievers':
      return ['recognition', 'new-starters', 'long-service-single', 'long-service-multiple']
        .includes(data.whatToPlay)
        ? `achievers:${data.whatToPlay}`
        : null;

    // #592 — Workday always fetches one complete Feed Manifest. Layout,
    // selection, QR, confidentiality, page cap, and looping are presentation
    // inputs and therefore share one stable preview-fetch key.
    case 'workday':
      return 'workday';

    default:
      return null;
  }
}

function shouldFetchDynamic(appTemplate, data, previousFetchKey, integrationId) {
  const fetchKey = getDynamicFetchKey(appTemplate, data, integrationId);
  return !!fetchKey && fetchKey !== previousFetchKey;
}

/**
 * The integration accounts are passed as a NAMED options object rather than
 * trailing positionals. shouldPublishDynamicError() already takes `isSample` in
 * position 4, so adding accounts positionally would have given two sibling
 * functions different meanings for the same slot — and a stray positional
 * argument would silently change the comparison instead of being ignored.
 *
 * @param {{requested?: *, current?: *}} [integrations]
 */
function shouldAcceptDynamicResponse(appTemplate, requestedData, currentData, integrations) {
  const requested = integrations && integrations.requested;
  const current = integrations && integrations.current;

  // #617 — key equality alone cannot separate two requests for the SAME account
  // and data. Selecting A → B → A before the first response lands starts three
  // requests, and both A responses share a key, so a slow first response could
  // overwrite the newer one. A monotonic generation makes each request
  // distinguishable; when generations are absent the check is key-only, as
  // before.
  const requestedGeneration = integrations && integrations.requestedGeneration;
  const currentGeneration = integrations && integrations.currentGeneration;
  if (requestedGeneration !== undefined
    && currentGeneration !== undefined
    && requestedGeneration !== currentGeneration) {
    return false;
  }

  const requestedFetchKey = getDynamicFetchKey(appTemplate, requestedData, requested);
  return !requestedFetchKey
    || requestedFetchKey === getDynamicFetchKey(appTemplate, currentData, current);
}

function shouldPublishDynamicError(appTemplate, requestedData, currentData, isSample, integrations) {
  return !isSample && shouldAcceptDynamicResponse(
    appTemplate, requestedData, currentData, integrations,
  );
}

function createDynamicFetchTracker() {
  let previousFetchKey;

  return {
    shouldFetch(appTemplate, data, integrationId) {
      const fetchKey = getDynamicFetchKey(appTemplate, data, integrationId);
      const shouldFetch = !!fetchKey && fetchKey !== previousFetchKey;
      if (shouldFetch) previousFetchKey = fetchKey;
      return shouldFetch;
    },
    prime(appTemplate, data, integrationId) {
      previousFetchKey = getDynamicFetchKey(appTemplate, data, integrationId);
    },
    reset() {
      previousFetchKey = undefined;
    },
  };
}

/**
 * isReadyForDynamicFetch(appTemplate, app, integration)
 *
 * #617 — an integration app's preview request is only meaningful once an
 * integration is selected: getDynamic() sends `credentials` and `integrationId`
 * pulled from it, so firing before the user picks an account produces an
 * unauthenticated request AND burns the tracker's fetch key, which then
 * suppresses the real request once the account is chosen.
 *
 * Templates whose integrationTemplate is absent (plain apps) or `rocket-`
 * prefixed (first-party feeds that need no account) are always ready.
 *
 * @param {{integrationTemplate?: string}|null|undefined} appTemplate
 * @param {{integrationId?: *, integration?: {id?: *}}|null|undefined} app
 * @param {{id?: *}|null|undefined} integration - the wrapper's bound integration
 * @returns {boolean}
 */
function isReadyForDynamicFetch(appTemplate, app, integration) {
    if (!appTemplate) return false;

    const integrationTemplate = appTemplate.integrationTemplate;
    if (!integrationTemplate || integrationTemplate.indexOf('rocket-') === 0) return true;

    return !!(
        (integration && integration.id)
        || (app && app.integrationId)
        || (app && app.integration && app.integration.id)
    );
}

/**
 * shouldFetchDynamicForCurrentData(previewDataSource, appTemplate, data, tracker)
 *
 * #561 — formly can write a template's default field value (e.g. Achievers'
 * default whatToPlay) into app.data before app-preview-wrapper's `loaded`
 * flips true. Its $watch('$ctrl.app.data') is gated on `loaded` and misses
 * that write, and nothing re-checks app.data once `loaded` becomes true, so
 * the default silently never triggers a live fetch until the user picks a
 * different value (a fresh mutation the now-unblocked watch does catch).
 *
 * Re-runs the same decision the watch would have made, for use right after
 * `loaded` flips true. Never fetches while previewing sample data.
 *
 * @param {string|undefined} previewDataSource
 * @param {{slug?: string}|null|undefined} appTemplate
 * @param {object|null|undefined} data
 * @param {{shouldFetch: Function}} tracker - a createDynamicFetchTracker() instance
 * @returns {boolean}
 */
function shouldFetchDynamicForCurrentData(previewDataSource, appTemplate, data, tracker, integrationId) {
  if (previewDataSource === 'sample') return false;
  if (!data) return false;
  return tracker.shouldFetch(appTemplate, data, integrationId);
}

// CommonJS export for Mocha unit tests (guarded so it is a no-op inside the
// concatenated Angular bundle, where `shouldFetchDynamic` is a plain global —
// mirrors the connect-strategy.js pattern from #634).
if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = {
        createDynamicFetchTracker,
        getDynamicFetchKey,
        isReadyForDynamicFetch,
        shouldAcceptDynamicResponse,
        shouldFetchDynamic,
        shouldFetchDynamicForCurrentData,
        shouldPublishDynamicError,
    };
}
